import { useEffect, useMemo, useState } from "react";
import { DatePicker, Input, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import {
  CalendarOutlined,
  EnvironmentOutlined,
  UserOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../shared/Inventory.module.css";
import InventoryPageHeader from "../shared/InventoryPageHeader";
import InventoryTable from "../shared/InventoryTable";
import { CREATE_jwel } from "../Config/Config";
import Header from "../Header";
import SidebarDrawer from "../SidebarDrawer";

const { RangePicker } = DatePicker;

/* ─── filter box theme palette (same as the other redesigned Accounts screens) ─── */
const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
  green: {
    text: "#15803d",
    bg: "#ffffff",
    border: "#86efac",
    ring: "rgba(21,128,61,0.15)",
  },
  amber: {
    text: "#b45309",
    bg: "#ffffff",
    border: "#fcd34d",
    ring: "rgba(180,83,9,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ─── Payable / Paid chips, same visual family as the other screens ─── */
const amountChipStyle = (kind) => {
  const palette = {
    payable: { bg: "#fef3c7", color: "#b45309", border: "#fcd34d" },
    paid: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  }[kind];

  return {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 12.5,
    fontWeight: 700,
    background: palette.bg,
    color: palette.color,
    border: `1px solid ${palette.border}`,
  };
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "RecNo", label: "Receipt No" },
  { key: "custname", label: "Party" },
  { key: "AREA", label: "Area" },
];
const secondaryFields = [
  { key: "PayableAmt", label: "Payable" },
  { key: "paidamt", label: "Paid" },
];

const RecieptRegister = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [searchArea, setSearchArea] = useState("");
  const [searchParty, setSearchParty] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");
  const [open, setOpen] = useState(false);
  const userArea = localStorage.getItem("city");
  const userName = localStorage.getItem("user");
  const singleImage = localStorage.getItem("singleImage");

  const toggleDrawer = () => {
    setOpen(false);
  };

  /* ---- data / API logic (unchanged endpoint, same query params) ---- */

  useEffect(() => {
    if (!dates[0] || !dates[1]) return;

    setLoading(true);
    setTableVisible(false);

    const fromDate = dayjs(dates[0]).format("YYYY-MM-DD");
    const toDate = dayjs(dates[1]).format("YYYY-MM-DD");

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetReceiptRegForData?fromDate=${fromDate}&toDate=${toDate}&street=%&custName=%`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        const calculatedData = detailsData.map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1,
        }));
        setData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching cash book details:", error);
      })
      .finally(() => setLoading(false));
  }, [dates]);

  // The original Area/Party Name selects were disconnected stubs (no
  // options, no state). Wired up as live client-side filters instead,
  // same pattern as the dimension filters on ProductWiseStock /
  // OutstandingCustomers — the API call itself is untouched.
  useEffect(() => {
    let filtered = [...data];

    if (searchArea) {
      const term = searchArea.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.AREA || "").toLowerCase().includes(term),
      );
    }

    if (searchParty) {
      const term = searchParty.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.custname || "").toLowerCase().includes(term),
      );
    }

    setFilteredData(
      filtered.map((item, index) => ({
        ...item,
        serialNo: index + 1,
      })),
    );
  }, [data, searchArea, searchParty]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    { title: "Receipt No", dataIndex: "RecNo", key: "RecNo" },
    {
      title: "Receipt Date",
      dataIndex: "RecDate",
      key: "RecDate",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    { title: "Area", dataIndex: "AREA", key: "AREA", align: "left" },
    {
      title: "Party Name",
      dataIndex: "custname",
      key: "custname",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    {
      title: "Payable Amount",
      dataIndex: "PayableAmt",
      key: "PayableAmt",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("payable")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Paid Amount",
      dataIndex: "paidamt",
      key: "paidamt",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("paid")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Cash",
      dataIndex: "PAIDCASH",
      key: "PAIDCASH",
      align: "right",
      render: (value) => (
        <span style={{ fontWeight: 600 }}>{Number(value || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Card",
      dataIndex: "PAIDCARD",
      key: "PAIDCARD",
      align: "right",
      render: (value) => (
        <span style={{ fontWeight: 600 }}>{Number(value || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Cheque",
      dataIndex: "PAIDCHEQUE",
      key: "PAIDCHEQUE",
      align: "right",
      render: (value) => (
        <span style={{ fontWeight: 600 }}>{Number(value || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Online",
      dataIndex: "PAIDONLINE",
      key: "PAIDONLINE",
      align: "right",
      render: (value) => (
        <span style={{ fontWeight: 600 }}>{Number(value || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "UPI",
      dataIndex: "PAIDUPI",
      key: "PAIDUPI",
      align: "right",
      render: (value) => (
        <span style={{ fontWeight: 600 }}>{Number(value || 0).toFixed(2)}</span>
      ),
    },
    { title: "Narration", dataIndex: "Narration", key: "Narration" },
  ];

  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        acc.payable += Number(item.PayableAmt || 0);
        acc.paid += Number(item.paidamt || 0);
        acc.cash += Number(item.PAIDCASH || 0);
        acc.card += Number(item.PAIDCARD || 0);
        acc.cheque += Number(item.PAIDCHEQUE || 0);
        acc.online += Number(item.PAIDONLINE || 0);
        acc.upi += Number(item.PAIDUPI || 0);
        return acc;
      },
      {
        payable: 0,
        paid: 0,
        cash: 0,
        card: 0,
        cheque: 0,
        online: 0,
        upi: 0,
      },
    );
  }, [filteredData]);

  const formattedData = useMemo(
    () =>
      filteredData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        PayableAmt: Number(item.PayableAmt || 0),
        paidamt: Number(item.paidamt || 0),
        PAIDCASH: Number(item.PAIDCASH || 0),
        PAIDCARD: Number(item.PAIDCARD || 0),
        PAIDCHEQUE: Number(item.PAIDCHEQUE || 0),
        PAIDONLINE: Number(item.PAIDONLINE || 0),
        PAIDUPI: Number(item.PAIDUPI || 0),
        RecDate: item?.RecDate ? dayjs(item.RecDate).format("DD/MMM/YYYY") : "",
      })),
    [filteredData],
  );

  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      RecNo: "",
      RecDate: "",
      AREA: "",
      custname: "",
      PayableAmt: totals.payable.toFixed(2),
      paidamt: totals.paid.toFixed(2),
      PAIDCASH: totals.cash.toFixed(2),
      PAIDCARD: totals.card.toFixed(2),
      PAIDCHEQUE: totals.cheque.toFixed(2),
      PAIDONLINE: totals.online.toFixed(2),
      PAIDUPI: totals.upi.toFixed(2),
      Narration: "",
    },
  ];

  return (
    <div className={styles.page}>
      <Header setOpen={setOpen} />
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="Receipt Register"
        subtitle="Accounts · Receipt Register"
        icon={<FileTextOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={filteredData.length}
        visible={filteredData.length > 0}
        stats={[
          { label: "Records", value: filteredData.length, color: "blue" },
          {
            label: "Payable",
            value: totals.payable.toFixed(2),
            color: "amber",
          },
          { label: "Paid", value: totals.paid.toFixed(2), color: "green" },
          { label: "Cash", value: totals.cash.toFixed(2), color: "teal" },
          { label: "Card", value: totals.card.toFixed(2), color: "orange" },
          { label: "Cheque", value: totals.cheque.toFixed(2), color: "red" },
          { label: "Online", value: totals.online.toFixed(2), color: "blue" },
          { label: "UPI", value: totals.upi.toFixed(2), color: "green" },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<CalendarOutlined />}
            label="Date Range"
            theme="blue"
          >
            <RangePicker
              value={dates}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDates(range);
              }}
              format="DD/MM/YYYY"
              size="middle"
              bordered={false}
              allowClear={false}
              style={{ width: 240 }}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          </FilterField>

          <FilterField
            icon={<EnvironmentOutlined />}
            label="Area"
            theme="green"
          >
            <Input
              allowClear
              size="middle"
              bordered={false}
              placeholder="Search area…"
              value={searchArea}
              onChange={(e) => setSearchArea(e.target.value)}
              style={{ width: 170 }}
            />
          </FilterField>

          <FilterField icon={<UserOutlined />} label="Party Name" theme="amber">
            <Input
              allowClear
              size="middle"
              bordered={false}
              placeholder="Search party name…"
              value={searchParty}
              onChange={(e) => setSearchParty(e.target.value)}
              style={{ width: 190 }}
            />
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="RecieptRegister"
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No receipts found for the selected filters"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.payable.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.paid.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.cash.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.card.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.cheque.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.online.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.upi.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell />
          </Table.Summary.Row>
        )}
      />
      <SidebarDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        singleImage={singleImage}
        userArea={userArea}
        userName={userName}
      />
    </div>
  );
};

export default RecieptRegister;
